/**
 * `mediaLibraryDto.ts` — wire shape of the account's **media library**: the
 * images and videos a workflow step points at.
 *
 * **Web-first — there is no Unity counterpart.** The desktop client has no
 * media library and no `media:` key on a workflow step, so nothing here is a
 * port. The shape is modelled on the DOCUMENT manifest next door
 * (`docsManifestDto.ts`) rather than invented, so the two customer-scoped
 * libraries read the same way and a future Unity port has one pattern to
 * follow: PascalCase JSON keys, unknown keys passed through so a newer client's
 * fields survive a read-modify-write, and an unreadable manifest reading as
 * EMPTY instead of throwing.
 *
 * Like a document, a media file has **no Dynamo row of its own** — it is two
 * artifacts, both customer-scoped:
 *
 * | # | Artifact | S3 key (under `account/{customerId}/projectdata/`) |
 * | - | -------- | -------------------------------------------------- |
 * | 1 | the file | `media/{fileName}` |
 * | 2 | this manifest | `media/index.json` |
 *
 * ## Why the URL is `campfire://media/…` and not an `https://` link
 *
 * The bucket is private and every read goes through Cognito credentials and the
 * S3 SDK, so there is no public object URL to copy and nothing in the web
 * client can mint a presigned one. A presigned URL would also EXPIRE, and this
 * string is pasted into a stored `workflow.yaml` that outlives any signature.
 *
 * So an uploaded file is addressed by {@link mediaLibraryUri} — a stable,
 * account-relative reference that {@link parseMediaLibraryUri} turns back into
 * a storage path, which the play panel resolves to a `blob:` URL through
 * `IMediaLibraryModel`. An `https://` URL the author already has works too and
 * is used verbatim; the scheme only exists for files this library holds.
 */

import { fileExtension } from "./docsManifestDto";

/** The customer-scoped folder every library file lives in. */
export const MEDIA_LIBRARY_FOLDER = "media";

/** The manifest's storage path (customer-scoped). */
export const MEDIA_LIBRARY_MANIFEST_STORAGE_PATH = `${MEDIA_LIBRARY_FOLDER}/index.json`;

/**
 * The URI scheme for library-hosted media. `campfire://media/{fileName}` —
 * deliberately the same shape as the storage path it maps to, so a reader who
 * has never seen this file can still guess where the bytes are.
 */
export const MEDIA_LIBRARY_URI_PREFIX = `campfire://${MEDIA_LIBRARY_FOLDER}/`;

/** What a library file is, for the workflow `media.type` it will be used as. */
export type MediaLibraryKind = "image" | "video";

/** Image extensions the library accepts and the play panel can render in an `<img>`. */
export const MEDIA_IMAGE_EXTENSIONS: ReadonlyArray<string> = [
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
  "avif",
  "bmp",
  "svg",
];

/**
 * Video extensions the library accepts. Kept to what a browser `<video>` can
 * actually decode — accepting an `.avi` would produce a library entry that
 * uploads fine and then plays nowhere.
 */
export const MEDIA_VIDEO_EXTENSIONS: ReadonlyArray<string> = ["mp4", "m4v", "webm", "ogv", "mov"];

export const MEDIA_LIBRARY_EXTENSIONS: ReadonlyArray<string> = [
  ...MEDIA_IMAGE_EXTENSIONS,
  ...MEDIA_VIDEO_EXTENSIONS,
];

export interface MediaLibraryEntryDto {
  FileName?: string;
  StoragePath?: string;
  /** `image` | `video`, decided at upload from the extension. */
  MediaType?: string;
  /** The `Content-Type` the bytes were uploaded with, so a read serves them back correctly. */
  ContentType?: string;
  FileSize?: number;
  /** `UserInfo.nameTag` of whoever uploaded it, same as an asset's `UserTag`. */
  UserTag?: string;
  /** Same InvariantCulture shape the docs manifest uses — see `formatDocLastModifiedUtc`. */
  LastModifiedUtc?: string;
  [k: string]: unknown;
}

export interface MediaLibraryManifestDto {
  Version?: number;
  Media?: MediaLibraryEntryDto[];
  [k: string]: unknown;
}

export function mediaLibraryStoragePath(fileName: string): string {
  return `${MEDIA_LIBRARY_FOLDER}/${fileName}`;
}

/** The reference an author pastes into `workflow.yaml`'s `media.url`. */
export function mediaLibraryUri(fileName: string): string {
  return `${MEDIA_LIBRARY_URI_PREFIX}${encodeURIComponent(fileName)}`;
}

/**
 * The storage path a `campfire://media/…` reference names, or null when the
 * value is any other kind of URL. Tolerant of an un-encoded name so a URI typed
 * by hand still resolves.
 */
export function parseMediaLibraryUri(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed.toLowerCase().startsWith(MEDIA_LIBRARY_URI_PREFIX)) return null;
  const raw = trimmed.slice(MEDIA_LIBRARY_URI_PREFIX.length);
  if (raw.length === 0) return null;
  let name = raw;
  try {
    name = decodeURIComponent(raw);
  } catch {
    // A stray `%` is not a reason to refuse the reference — the literal name
    // is what the manifest holds anyway.
  }
  return mediaLibraryStoragePath(name);
}

/** The kind a file name implies, or null when the extension is not supported. */
export function mediaKindForFileName(fileName: string | undefined): MediaLibraryKind | null {
  const ext = fileExtension(fileName);
  if (MEDIA_IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (MEDIA_VIDEO_EXTENSIONS.includes(ext)) return "video";
  return null;
}

/**
 * `Content-Type` for an upload. Unlike a document this genuinely matters: the
 * bytes come back through a `blob:` URL, and a `<video>` handed
 * `application/octet-stream` refuses to play.
 */
export function mediaContentType(fileName: string | undefined): string {
  switch (fileExtension(fileName)) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    case "avif":
      return "image/avif";
    case "bmp":
      return "image/bmp";
    case "svg":
      return "image/svg+xml";
    case "mp4":
    case "m4v":
      return "video/mp4";
    case "webm":
      return "video/webm";
    case "ogv":
      return "video/ogg";
    case "mov":
      return "video/quicktime";
    default:
      return "application/octet-stream";
  }
}

function normalizeStoragePath(storagePath: string | undefined | null): string | null {
  const trimmed = storagePath?.trim();
  return trimmed ? trimmed : null;
}

/** Replace by `StoragePath` (case-insensitive), else append. */
export function upsertMediaEntry(
  manifest: MediaLibraryManifestDto,
  entry: MediaLibraryEntryDto,
): void {
  if (!manifest.Media) manifest.Media = [];
  const storagePath = normalizeStoragePath(entry.StoragePath);
  if (!storagePath) return;
  const key = storagePath.toLowerCase();
  const index = manifest.Media.findIndex(
    (m) => normalizeStoragePath(m?.StoragePath)?.toLowerCase() === key,
  );
  if (index >= 0) manifest.Media[index] = entry;
  else manifest.Media.push(entry);
}

export function findMediaEntry(
  manifest: MediaLibraryManifestDto | null | undefined,
  storagePath: string,
): MediaLibraryEntryDto | null {
  const key = normalizeStoragePath(storagePath)?.toLowerCase();
  if (!key || !manifest?.Media) return null;
  return (
    manifest.Media.find((m) => normalizeStoragePath(m?.StoragePath)?.toLowerCase() === key) ?? null
  );
}

export function removeMediaEntry(
  manifest: MediaLibraryManifestDto,
  storagePath: string,
): boolean {
  const key = normalizeStoragePath(storagePath)?.toLowerCase();
  if (!key || !manifest.Media) return false;
  const before = manifest.Media.length;
  manifest.Media = manifest.Media.filter(
    (m) => normalizeStoragePath(m?.StoragePath)?.toLowerCase() !== key,
  );
  return manifest.Media.length !== before;
}

/**
 * Parses the manifest. Never throws: an unreadable manifest reads as EMPTY,
 * the same rule `parseDocsManifest` follows. Entries with no name or path are
 * dropped — there is nothing a card could address.
 */
export function parseMediaLibraryManifest(json: string | null | undefined): MediaLibraryManifestDto {
  if (!json?.trim()) return { Version: 1, Media: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    console.warn("[mediaLibrary] manifest is not valid JSON — treating as empty");
    return { Version: 1, Media: [] };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { Version: 1, Media: [] };
  }
  const manifest = parsed as MediaLibraryManifestDto;
  const media = Array.isArray(manifest.Media) ? manifest.Media : [];
  manifest.Media = media.filter(
    (m): m is MediaLibraryEntryDto =>
      !!m &&
      typeof m === "object" &&
      !!normalizeStoragePath(m.StoragePath) &&
      !!m.FileName?.trim(),
  );
  if (typeof manifest.Version !== "number") manifest.Version = 1;
  return manifest;
}

export function serializeMediaLibraryManifest(manifest: MediaLibraryManifestDto): string {
  return JSON.stringify({ ...manifest, Media: manifest.Media ?? [] });
}

/**
 * A file name that cannot collide with another upload and cannot escape the
 * `media/` prefix. The library is customer-scoped and flat, so two people
 * uploading `clip.mp4` must not overwrite each other — a short random suffix is
 * cheaper than a per-file existence check, and keeps the readable name in the
 * URL the author pastes.
 */
export function uniqueMediaFileName(originalName: string, suffix: string): string {
  const safe = originalName
    .replace(/[/\\]/g, "-")
    .replace(/[^\w.\-() ]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  const dot = safe.lastIndexOf(".");
  const stem = dot > 0 ? safe.slice(0, dot) : safe;
  const ext = dot > 0 ? safe.slice(dot) : "";
  const base = stem.length > 0 ? stem : "media";
  return `${base}-${suffix}${ext.toLowerCase()}`;
}
